/**
 * Elites — champion variants and unique packs, as data.
 *
 * D2 promotes an ordinary monster in one of two ways, and they are exclusive:
 *
 *   - a **champion** takes one of five tiers (Champion, Berserker, Fanatic,
 *     Ghostly, Possessed), which is a pure stat swap and brings no minions;
 *   - a **unique** takes boss modifiers — one in Normal, two in Nightmare,
 *     three in Hell — and brings a group of minions that share some of them.
 *
 * Everything below the types is a table. Adding a modifier is a row, gating one
 * is a field, and the only `if`s in the file are the two rules D2 states as
 * rules: a group may land at most once, and a bonus may not hand a monster a
 * third immunity.
 *
 * ## Where the numbers come from
 *
 * Every figure here is cited at the row that uses it. Two independent sources
 * carry the whole table and agree on it:
 *
 *   - Maxroll, "Elite Groups & Act Bosses" (https://maxroll.gg/d2/resources/elite-monster)
 *     — the D2R-current champion/unique tables and per-modifier values;
 *   - the Amazon Basin's "Bosses" compendium (https://www.mannm.org/d2library/faqtoids/boss_eng.html)
 *     — the same tables from the 1.10 game files, and the immunity rule.
 *
 * Where they disagree, or where only one of them states something, the row says
 * so rather than picking a winner silently. Unverified numbers are marked and
 * filed in `TODO.md`; nothing here is written from memory.
 *
 * ## What is declared but inert
 *
 * Six modifiers need mechanics core does not have (auras, curses, teleport,
 * missiles, a mana pool). They are declared with their real gating and an
 * `unimplemented` note, and contribute nothing — a table row that lies about
 * what it pays would be worse than the gap. Three more pay their arithmetic and
 * drop a second half (an explosion, a volley, a chill). `TODO.md` rows 250-259
 * track every gap and every place the two sources disagree.
 */
import { aggregateModifiers, modifierTotal } from './items.js';
import { IMMUNITY_THRESHOLD, mapDamageTypes, } from './stats.js';
const PERCENT = 100;
/** The three difficulties, in the order every D2 table prints them. */
export const ELITE_DIFFICULTIES = ['normal', 'nightmare', 'hell'];
/**
 * The monster archetypes of `docs/GAME-DESIGN.md`. D2 gates a few modifiers on
 * what the monster can physically do — a monster with no missile cannot fire
 * three of them — and this is the axis that gate hangs off.
 */
export const ELITE_ARCHETYPES = ['melee', 'ranged', 'caster'];
/**
 * How many modifiers a unique boss draws.
 *
 * Source: Maxroll ("Normal: 1 affix, Nightmare: 2 affixes, Hell: 3 affixes");
 * the Basin states the same rule in prose ("In Normal difficulty randomly
 * generated bosses get one special bonus, in Nightmare two and in Hell three").
 */
export const ELITE_AFFIX_COUNT = {
    normal: 1,
    nightmare: 2,
    hell: 3,
};
/** Minions per unique pack, inclusive. Source: Maxroll, "Minions: 3-6 per group". */
export const MINION_COUNT_MIN = 3;
export const MINION_COUNT_MAX = 6;
/**
 * D2 refuses to let a boss modifier hand out a third immunity.
 *
 * Source: the Basin, verbatim — "If a special bonus would lead to three ore
 * more immunities the immunities granted by additional boni are skipped."
 * Maxroll states the outcome from the player's side: "Unique Monsters can spawn
 * with modifiers that give them an immunity in addition to the ones listed, for
 * a total maximum of two immunities" (https://maxroll.gg/d2/resources/immunities).
 */
export const MAX_ELITE_IMMUNITIES = 2;
// ---------------------------------------------------------------------------
// Table builders. Every row below is written with these, so a row is data.
// ---------------------------------------------------------------------------
/** A number that does not move with difficulty. */
const flat = (value) => ({ normal: value, nightmare: value, hell: value });
/** A number that does, written Normal → Nightmare → Hell as the sources print it. */
const perDifficulty = (normal, nightmare, hell) => ({
    normal,
    nightmare,
    hell,
});
/** Life untouched, no bonus, no level gain: what a source that changes nothing contributes. */
const NEUTRAL_SCALING = {
    lifePercent: flat(PERCENT),
    attackRatingPercent: flat(0),
    movementSpeedPercent: flat(0),
    levelBonus: flat(0),
};
const scaling = (over = {}) => ({
    lifePercent: over.lifePercent ?? NEUTRAL_SCALING.lifePercent,
    attackRatingPercent: over.attackRatingPercent ?? NEUTRAL_SCALING.attackRatingPercent,
    movementSpeedPercent: over.movementSpeedPercent ?? NEUTRAL_SCALING.movementSpeedPercent,
    levelBonus: over.levelBonus ?? NEUTRAL_SCALING.levelBonus,
});
const effect = (over = {}) => ({
    modifiers: over.modifiers ?? [],
    overrides: over.overrides ?? [],
    elementalDamage: over.elementalDamage ?? null,
    scaling: over.scaling ?? NEUTRAL_SCALING,
});
/** An effect that pays nothing at all — what every inert row hands to both roles. */
const INERT = effect();
const modifier = (stat, value, damageType = null) => ({ stat, damageType, value });
/** Resistance in percent, per type. The one shape D2 grants resistance in. */
const resistance = (damageType, value) => modifier('resistancePercent', flat(value), damageType);
const elemental = (damageType, minPercentOfLevel, maxPercentOfLevel, difficulties = []) => ({ damageType, minPercentOfLevel, maxPercentOfLevel, difficulties });
/**
 * D2's two level-scaled damage brackets, which every enchantment reuses:
 * a boss gets `min = mlvl x 66/100, max = mlvl`, its minions half of that
 * (`min = mlvl x 33/100, max = mlvl x 50/100`) and only in Nightmare and Hell.
 *
 * Source: Maxroll, per-modifier tables; the Basin states the same restriction
 * ("Nightmare and Hell only" for the minion half of the enchanted modifiers).
 */
const BOSS_MIN_PERCENT = 66;
const BOSS_MAX_PERCENT = 100;
const MINION_MIN_PERCENT = 33;
const MINION_MAX_PERCENT = 50;
const MINION_ELEMENTAL_DIFFICULTIES = ['nightmare', 'hell'];
const bossElemental = (damageType) => elemental(damageType, BOSS_MIN_PERCENT, BOSS_MAX_PERCENT);
const minionElemental = (damageType) => elemental(damageType, MINION_MIN_PERCENT, MINION_MAX_PERCENT, MINION_ELEMENTAL_DIFFICULTIES);
// ---------------------------------------------------------------------------
// The champion tiers.
// ---------------------------------------------------------------------------
/**
 * The profile four of the five tiers share, and the Berserker's trade.
 *
 * Source: Maxroll's champion table — Champion/Fanatic/Ghostly life 300/250/200%,
 * attack rating +67/56/49%, damage +90/75/66%, `mLVL = Area Level +2`; Possessed
 * the same but 600/500/400% life; Berserker 75/62.5/50% life with +270/225/198%
 * to both damage and attack rating at `mLVL = Area Level +3`. The Basin's table
 * carries the identical life column (it prints the Berserker's Nightmare figure
 * as a rounded 62%; 62.5 is kept here because the Berserker is exactly a quarter
 * of a Champion in all three difficulties).
 */
const CHAMPION_LIFE = perDifficulty(300, 250, 200);
const CHAMPION_ATTACK_RATING = perDifficulty(67, 56, 49);
const CHAMPION_DAMAGE = perDifficulty(90, 75, 66);
const CHAMPION_LEVEL_BONUS = flat(2);
/**
 * Maxroll prints "Run/Walk +20%" under Champion and "+100%" under Fanatic, and
 * nothing for the other three. Every one of the five *is* a champion, so the
 * +20% is read here as the rank's baseline and the Fanatic's +100% as its
 * stated total. Unverified for Berserker, Ghostly and Possessed — TODO row 257.
 */
const CHAMPION_RUN_WALK = flat(20);
const FANATIC_RUN_WALK = flat(100);
/** Fanatic and Ghostly both have "Defense: -70%" (Maxroll); the Basin calls it "defense set to -70". */
const CHAMPION_DEFENSE_PENALTY = modifier('enhancedDefensePercent', flat(-70));
/** Champion damage is enhanced damage: it lifts physical damage and leaves the elemental flat, as D2 does. */
const enhancedDamage = (value) => modifier('enhancedDamagePercent', value);
const championScaling = (life, runWalk, levelBonus) => scaling({
    lifePercent: life,
    attackRatingPercent: CHAMPION_ATTACK_RATING,
    movementSpeedPercent: runWalk,
    levelBonus,
});
/**
 * The five champion tiers, in the order the sources list them.
 *
 * Not modelled, and not pretended: the Ghostly tier's 6-second cold length
 * (core has no chill) — TODO row 253.
 */
export const CHAMPION_TIERS = [
    {
        id: 'champion',
        label: 'Champion',
        effect: effect({
            modifiers: [enhancedDamage(CHAMPION_DAMAGE)],
            scaling: championScaling(CHAMPION_LIFE, CHAMPION_RUN_WALK, CHAMPION_LEVEL_BONUS),
        }),
    },
    {
        id: 'berserker',
        label: 'Berserker',
        effect: effect({
            modifiers: [enhancedDamage(perDifficulty(270, 225, 198))],
            scaling: scaling({
                lifePercent: perDifficulty(75, 62.5, 50),
                attackRatingPercent: perDifficulty(270, 225, 198),
                movementSpeedPercent: CHAMPION_RUN_WALK,
                levelBonus: flat(3),
            }),
        }),
    },
    {
        id: 'fanatic',
        label: 'Fanatic',
        effect: effect({
            modifiers: [enhancedDamage(CHAMPION_DAMAGE), CHAMPION_DEFENSE_PENALTY],
            scaling: championScaling(CHAMPION_LIFE, FANATIC_RUN_WALK, CHAMPION_LEVEL_BONUS),
        }),
    },
    {
        id: 'ghostly',
        label: 'Ghostly',
        effect: effect({
            modifiers: [enhancedDamage(CHAMPION_DAMAGE), CHAMPION_DEFENSE_PENALTY],
            // "Damage Resist: 80% (replaces other values)" — Maxroll. A set, not a sum.
            overrides: [resistance('physical', 80)],
            // "Cold Damage: Min mLVL x 33/100, Max mLVL x 50/100" — Maxroll.
            elementalDamage: elemental('cold', MINION_MIN_PERCENT, MINION_MAX_PERCENT),
            scaling: championScaling(CHAMPION_LIFE, CHAMPION_RUN_WALK, CHAMPION_LEVEL_BONUS),
        }),
    },
    {
        id: 'possessed',
        label: 'Possessed',
        effect: effect({
            modifiers: [enhancedDamage(CHAMPION_DAMAGE)],
            scaling: championScaling(perDifficulty(600, 500, 400), CHAMPION_RUN_WALK, CHAMPION_LEVEL_BONUS),
        }),
    },
];
/**
 * What the rank itself is worth, before any tier or modifier.
 *
 * A champion's numbers all live on its tier, so the rank contributes nothing;
 * a unique's life and level are the rank's, and its minions get their own life
 * column. `null` is how "a champion has no minions" is stated as data.
 *
 * Source: Maxroll — unique `mLVL = Area Level +3`, boss life 400/300/200%,
 * minion life 200/175/150%; the Basin's table carries the identical columns.
 * That the minions share the boss's +3 is *not* stated by either source and is
 * kept only because it is the behaviour the suite pins — TODO row 258.
 */
const UNIQUE_BOSS_SCALING = scaling({ lifePercent: perDifficulty(400, 300, 200), levelBonus: flat(3) });
const RANK_SCALING = {
    champion: { boss: NEUTRAL_SCALING, minion: null },
    unique: {
        boss: UNIQUE_BOSS_SCALING,
        minion: scaling({ lifePercent: perDifficulty(200, 175, 150), levelBonus: flat(3) }),
    },
};
// ---------------------------------------------------------------------------
// The modifier table.
// ---------------------------------------------------------------------------
/**
 * The auras an Aura Enchanted boss draws from.
 *
 * Source: Maxroll — "Selected from Paladin auras (Blessed Aim, Conviction,
 * Fanaticism, Holy Fire, Holy Freeze, Holy Shock, Might)", with "holyshock can
 * only be selected when the boss mLVL >= 20". Older wiki write-ups list only
 * five (no Conviction, no Fanaticism) and add a general mlvl-4 floor; neither
 * claim is adopted here — the omission is contradicted by the same page's own
 * prose, and the floor is unconfirmed, so the ungated rows carry no gate rather
 * than an invented one (TODO row 252).
 */
export const ELITE_AURAS = [
    { id: 'blessed-aim', label: 'Blessed Aim', requiredLevel: 1 },
    { id: 'conviction', label: 'Conviction', requiredLevel: 1 },
    { id: 'fanaticism', label: 'Fanaticism', requiredLevel: 1 },
    { id: 'holy-fire', label: 'Holy Fire', requiredLevel: 1 },
    { id: 'holy-freeze', label: 'Holy Freeze', requiredLevel: 1 },
    { id: 'holy-shock', label: 'Holy Shock', requiredLevel: 20 },
    { id: 'might', label: 'Might', requiredLevel: 1 },
];
/** The elements Spectral Hit draws from — D2's five, less the magic one core has no type for. */
const SPECTRAL_DAMAGE_TYPES = ['fire', 'cold', 'lightning', 'poison'];
const affixRow = (row) => ({
    id: row.id,
    label: row.label,
    group: row.group,
    difficulties: row.difficulties ?? [],
    archetypes: row.archetypes ?? [],
    boss: row.boss ?? INERT,
    minion: row.minion ?? INERT,
    unimplemented: row.unimplemented,
});
/** The archetypes that fire something. D2 gates its missile modifiers on exactly this. */
const MISSILE_ARCHETYPES = ['ranged', 'caster'];
/**
 * The boss modifiers.
 *
 * Every row sits in its own group: D2 lets Fire, Cold and Lightning Enchanted
 * land on one boss together — that stacking is how a Hell boss ends up
 * double-immune, and the immunity rule below exists precisely because it can.
 * The group column is here for content that must not collide (a second tier of
 * a row, a variant), and it is what the roller excludes on.
 */
export const ELITE_AFFIXES = [
    affixRow({
        id: 'extra-fast',
        label: 'Extra Fast',
        group: 'speed',
        // "+100% Fast Run Walk" — Maxroll; the Basin reads it as "doubles (attack
        // and) movement speed", parenthetical in the source. Only the movement half
        // is declared; whether it also grants attack speed is unresolved (TODO 251).
        boss: effect({ scaling: scaling({ movementSpeedPercent: flat(100) }) }),
        minion: effect({ scaling: scaling({ movementSpeedPercent: flat(100) }) }),
        unimplemented: 'Declares its +100% movement but cannot spend it: CombatantStats carries no movement speed, so whoever spawns the monster must read resolveScaling().movementSpeedPercent and scale the walker speed itself. Sources also disagree on whether Extra Fast raises attack speed.',
    }),
    affixRow({
        id: 'extra-strong',
        label: 'Extra Strong',
        group: 'strength',
        // Maxroll and the Basin agree digit for digit: boss damage +135/112/99% and
        // attack rating +90/75/66%; minions +67/56/49% and +45/37/33%.
        boss: effect({
            modifiers: [enhancedDamage(perDifficulty(135, 112, 99))],
            scaling: scaling({ attackRatingPercent: perDifficulty(90, 75, 66) }),
        }),
        minion: effect({
            modifiers: [enhancedDamage(perDifficulty(67, 56, 49))],
            scaling: scaling({ attackRatingPercent: perDifficulty(45, 37, 33) }),
        }),
        unimplemented: null,
    }),
    affixRow({
        id: 'cursed',
        label: 'Cursed',
        group: 'curse',
        unimplemented: 'D2 gives a 75% chance to cast Amplify Damage at skill level (mlvl/5)+1 on hit (Maxroll). Core has no curse and no on-hit hook: effects.ts has the timed ModifierEffect a curse would be, but nothing routes a monster swing into a victim’s vitals.',
    }),
    affixRow({
        id: 'aura-enchanted',
        label: 'Aura Enchanted',
        group: 'aura',
        unimplemented: 'The aura pool is declared in ELITE_AURAS with its mlvl gate, but nothing selects an aura, and core has no aura: no area-of-effect buff, no radius, no membership. The pack carries no aura yet.',
    }),
    affixRow({
        id: 'lightning-enchanted',
        label: 'Lightning Enchanted',
        group: 'lightningEnchant',
        // Maxroll lists +75% lightning resistance for the boss and the level-scaled
        // damage for the minions only — the boss's own payout is the charged bolts,
        // which core cannot fire.
        boss: effect({ modifiers: [resistance('lightning', 75)] }),
        minion: effect({ elementalDamage: minionElemental('lightning') }),
        unimplemented: 'The boss fires 8 charged bolts of 2x(mlvl/2)-1 damage when it takes a hit (Maxroll). Core has no missiles and no on-damaged hook, so only the resistance and the minions’ added damage are paid.',
    }),
    affixRow({
        id: 'cold-enchanted',
        label: 'Cold Enchanted',
        group: 'coldEnchant',
        boss: effect({ modifiers: [resistance('cold', 75)], elementalDamage: bossElemental('cold') }),
        minion: effect({ elementalDamage: minionElemental('cold') }),
        unimplemented: 'The cold length of (mlvl x 0.2)+4 seconds (Maxroll) is dropped: core has no chill or freeze, so the damage lands but the slow does not.',
    }),
    affixRow({
        id: 'fire-enchanted',
        label: 'Fire Enchanted',
        group: 'fireEnchant',
        boss: effect({ modifiers: [resistance('fire', 75)], elementalDamage: bossElemental('fire') }),
        minion: effect({ elementalDamage: minionElemental('fire') }),
        unimplemented: 'The corpse explosion on death (Maxroll: 5.625-9.375% of life in Normal down to 0.375-0.625% in Hell, radius 2.66-4 yards) is not modelled — core has no death trigger and no area damage. The Basin also reports Fire Enchanted doubling attack rating, which Maxroll does not list; unresolved, so not paid (TODO row 254).',
    }),
    affixRow({
        id: 'magic-resistant',
        label: 'Magic Resistant',
        group: 'elementalResist',
        // "Nightmare/Hell only, non-Super Uniques" — Maxroll. Fire, cold and
        // lightning, +40 each; explicitly not poison and not physical, which the
        // Fandom archive confirms from the player's side ("does not reduce poison
        // resist"). That same page states the grant lands "only if it is below
        // 100%", which the Basin's rule contradicts and which cannot explain a
        // 100/100/70 boss keeping its 70 — see `grantResistance` and TODO row 256.
        difficulties: ['nightmare', 'hell'],
        boss: effect({
            modifiers: [resistance('fire', 40), resistance('cold', 40), resistance('lightning', 40)],
        }),
        unimplemented: null,
    }),
    affixRow({
        id: 'stone-skin',
        label: 'Stone Skin',
        group: 'armour',
        // "Physical Damage Resist: 50%, Defense: +200% base" — Maxroll; the Basin
        // settles the ambiguity of that second figure: "doubles base defense", with
        // "physical +50%" reaching the minions too.
        boss: effect({
            modifiers: [modifier('enhancedDefensePercent', flat(100)), resistance('physical', 50)],
        }),
        minion: effect({ modifiers: [resistance('physical', 50)] }),
        unimplemented: null,
    }),
    affixRow({
        id: 'multiple-shots',
        label: 'Multiple Shots',
        group: 'missiles',
        archetypes: MISSILE_ARCHETYPES,
        unimplemented: 'The boss fires 3 missiles instead of 1 (Maxroll). combat.ts resolves one swing against one defender and core has no missile entity, so there is nothing to multiply.',
    }),
    affixRow({
        id: 'teleportation',
        label: 'Teleportation',
        group: 'mobility',
        archetypes: MISSILE_ARCHETYPES,
        unimplemented: 'D2 teleports the monster away when it is meleed and restores up to 30% of its life (Maxroll). Core has no teleport, no monster AI hook and no self-heal on a monster.',
    }),
    affixRow({
        id: 'spectral-hit',
        label: 'Spectral Hit',
        group: 'spectral',
        // The element is drawn once, when the monster spawns, and the damage uses
        // the same bracket as the enchantments: min mlvl x 66/100, max mlvl.
        boss: effect({ elementalDamage: bossElemental(null) }),
        minion: effect({ elementalDamage: minionElemental(null) }),
        unimplemented: 'D2 draws the element from five (fire, cold, lightning, poison and magic) and core has no magic damage type, so this row draws from four. The D2R wikis also report x2 chance to hit and a conditional +20 to fire/cold/lightning resistance below 75, which Maxroll does not list and which are not paid (TODO row 255).',
    }),
    affixRow({
        id: 'mana-burn',
        label: 'Mana Burn',
        group: 'manaBurn',
        unimplemented: 'A hit burns mana (Maxroll: min mlvl x 66/100, max mlvl) and the boss gains 20% magic resistance. Core has neither: effects.ts models a life pool only, and there is no magic damage type to resist.',
    }),
];
// ---------------------------------------------------------------------------
// Lookups and gating.
// ---------------------------------------------------------------------------
/**
 * The tier with this id. An unknown id is a programmer error, not an outcome.
 *
 * Both lookups below take an id and nothing else — no pool parameter. They
 * search the canonical table by definition (throwing for an id that is not in
 * it only means anything against that table), and a one-argument function is
 * the one a caller can hand straight to `map`.
 */
export const championTierById = (id) => {
    const found = CHAMPION_TIERS.find((tier) => tier.id === id);
    if (found === undefined)
        throw new RangeError(`Unknown champion tier: ${id}`);
    return found;
};
/** The modifier row with this id. Unknown is a programmer error, as above. */
export const eliteAffixById = (id) => {
    const found = ELITE_AFFIXES.find((affix) => affix.id === id);
    if (found === undefined)
        throw new RangeError(`Unknown elite affix: ${id}`);
    return found;
};
/**
 * Everything a monster of this archetype may roll at this difficulty. An empty
 * gate list means "no gate", which is how most rows are written.
 */
export const eligibleEliteAffixes = (difficulty, archetype, pool = ELITE_AFFIXES) => pool.filter((affix) => (affix.difficulties.length === 0 || affix.difficulties.includes(difficulty)) &&
    (affix.archetypes.length === 0 || affix.archetypes.includes(archetype)));
// ---------------------------------------------------------------------------
// Resolving a row against a difficulty and a level.
// ---------------------------------------------------------------------------
const resolveScalingRow = (source, difficulty) => ({
    lifePercent: source.lifePercent[difficulty],
    attackRatingPercent: source.attackRatingPercent[difficulty],
    movementSpeedPercent: source.movementSpeedPercent[difficulty],
    levelBonus: source.levelBonus[difficulty],
});
/**
 * Merges two resolved scalings, each in its own unit: life multiplies (400% of
 * a 300% pool is 1200%), the rest add.
 */
const mergeScaling = (left, right) => ({
    lifePercent: (left.lifePercent * right.lifePercent) / PERCENT,
    attackRatingPercent: left.attackRatingPercent + right.attackRatingPercent,
    movementSpeedPercent: left.movementSpeedPercent + right.movementSpeedPercent,
    levelBonus: left.levelBonus + right.levelBonus,
});
const RESOLVED_NEUTRAL = resolveScalingRow(NEUTRAL_SCALING, 'normal');
const resolveModifier = (row, difficulty) => ({
    stat: row.stat,
    damageType: row.damageType,
    value: row.value[difficulty],
});
/** `mlvl x percent/100`, floored — D2's integer arithmetic. */
const levelDamage = (level, percentOfLevel) => Math.floor((level * percentOfLevel) / PERCENT);
/**
 * Turns a level-scaled bracket into the two flat damage modifiers it pays.
 * Nothing comes out when the bracket does not apply at this difficulty, or when
 * the row draws its element and no element was drawn.
 */
const elementalModifiers = (source, difficulty, level, drawn) => {
    if (source === null)
        return [];
    if (source.difficulties.length > 0 && !source.difficulties.includes(difficulty))
        return [];
    const damageType = source.damageType ?? drawn;
    if (damageType === null)
        return [];
    return [
        { stat: 'damageMin', damageType, value: levelDamage(level, source.minPercentOfLevel) },
        { stat: 'damageMax', damageType, value: levelDamage(level, source.maxPercentOfLevel) },
    ];
};
const resolveEffect = (source, difficulty, level, drawn) => ({
    modifiers: aggregateModifiers([
        ...source.modifiers.map((row) => resolveModifier(row, difficulty)),
        ...elementalModifiers(source.elementalDamage, difficulty, level, drawn),
    ]),
    overrides: source.overrides.map((row) => resolveModifier(row, difficulty)),
    scaling: resolveScalingRow(source.scaling, difficulty),
});
// ---------------------------------------------------------------------------
// The roll.
// ---------------------------------------------------------------------------
const drawsItsElement = (definition) => definition.boss.elementalDamage?.damageType === null || definition.minion.elementalDamage?.damageType === null;
/**
 * Rolls one row onto a pack. The element is drawn once and shared by the boss
 * and its minions — D2 fixes it when the monster spawns, not per swing — and
 * only rows that need one draw at all, so the stream stays a function of the
 * table rather than of the pool's shape.
 */
const rollEliteAffix = (definition, difficulty, level, rng) => {
    const drawn = drawsItsElement(definition)
        ? // The list is a non-empty constant and the draw is in range, so the
            // fallback is unreachable; it exists because the index type admits it.
            (SPECTRAL_DAMAGE_TYPES[rng.int(0, SPECTRAL_DAMAGE_TYPES.length)] ?? null)
        : null;
    return {
        id: definition.id,
        label: definition.label,
        group: definition.group,
        boss: resolveEffect(definition.boss, difficulty, level, drawn),
        minion: resolveEffect(definition.minion, difficulty, level, drawn),
        unimplemented: definition.unimplemented,
    };
};
const takeCandidate = (candidates, rng) => (candidates.length === 0 ? undefined : candidates[rng.int(0, candidates.length)]);
/**
 * Draws up to `count` rows, uniformly over what the gates allow, never twice
 * from one group. A pool too shallow to fill the quota yields fewer rows — a
 * content gap is not a runtime error, exactly as `rollAffixes` treats it.
 */
const rollEliteAffixes = (count, difficulty, archetype, level, rng, pool) => {
    const rolled = [];
    let candidates = eligibleEliteAffixes(difficulty, archetype, pool);
    for (let drawn = 0; drawn < count; drawn += 1) {
        const chosen = takeCandidate(candidates, rng);
        if (chosen === undefined)
            break;
        rolled.push(rollEliteAffix(chosen, difficulty, level, rng));
        candidates = candidates.filter((candidate) => candidate.group !== chosen.group);
    }
    return rolled;
};
/**
 * Rolls a pack.
 *
 * Draw order is fixed, so a replay consumes the identical slice of the stream:
 * a champion draws its tier and stops; a unique draws its modifiers one at a
 * time (plus one draw for a row that picks its element) and then its minion
 * count.
 *
 * `affixes` and `tiers` are the pools the draw happens *from*, not a roster to
 * apply: the difficulty still decides how many rows are drawn, so a two-row
 * pool in Normal yields one row and the second is simply never seen. Narrowing
 * the pool is how a caller pins a pack to particular content without a second
 * entry point.
 */
export const rollElite = (request, rng, affixes = ELITE_AFFIXES, tiers = CHAMPION_TIERS) => {
    const { rank, level } = request;
    const difficulty = request.difficulty ?? 'normal';
    const archetype = request.archetype ?? 'melee';
    if (!Number.isInteger(level) || level < 1) {
        throw new RangeError(`Monster level must be a positive integer, received ${level}`);
    }
    if (rank === 'champion') {
        const tier = tiers[rng.int(0, Math.max(1, tiers.length))];
        if (tier === undefined)
            throw new RangeError('A champion needs at least one tier to draw from');
        return {
            rank,
            difficulty,
            archetype,
            level: level + tier.effect.scaling.levelBonus[difficulty],
            champion: tier,
            affixes: [],
            minions: 0,
        };
    }
    const monsterLevel = level + UNIQUE_BOSS_SCALING.levelBonus[difficulty];
    return {
        rank,
        difficulty,
        archetype,
        level: monsterLevel,
        champion: null,
        affixes: rollEliteAffixes(ELITE_AFFIX_COUNT[difficulty], difficulty, archetype, monsterLevel, rng, affixes),
        minions: rng.int(MINION_COUNT_MIN, MINION_COUNT_MAX + 1),
    };
};
// ---------------------------------------------------------------------------
// Applying a pack.
// ---------------------------------------------------------------------------
/** Every source that pays this role: the rank, the champion tier if any, and each modifier's half. */
const effectsFor = (pack, role) => {
    const rank = RANK_SCALING[pack.rank][role];
    if (rank === null)
        throw new RangeError(`A ${pack.rank} pack has no ${role} to apply`);
    const tier = pack.champion === null ? [] : [resolveEffect(pack.champion.effect, pack.difficulty, pack.level, null)];
    return [
        { modifiers: [], overrides: [], scaling: resolveScalingRow(rank, pack.difficulty) },
        ...tier,
        ...pack.affixes.map((affix) => affix[role]),
    ];
};
/**
 * What this pack does to the numbers the item vocabulary cannot carry.
 *
 * Throws for a champion's minions, because there are none — asking is a
 * programmer error, not an empty result.
 */
export const resolveScaling = (pack, role) => effectsFor(pack, role).reduce((total, source) => mergeScaling(total, source.scaling), RESOLVED_NEUTRAL);
/**
 * The pack's contribution in the item vocabulary, so a monster's modifiers sum
 * exactly the way gear and buffs do.
 */
export const eliteModifiers = (pack, role) => aggregateModifiers(effectsFor(pack, role).flatMap((source) => source.modifiers));
/** The life pool this rank carries, from the life the base monster would have had. */
export const eliteLife = (life, pack, role) => Math.floor((life * resolveScaling(pack, role).lifePercent) / PERCENT);
const immunityCount = (resistances) => Object.values(resistances).filter((value) => value >= IMMUNITY_THRESHOLD).length;
/**
 * Adds one source's resistance grant, unless it would hand out an immunity past
 * D2's limit of two — in which case the whole grant is skipped, which is what
 * "the immunities granted by additional boni are skipped" means for a bonus
 * that grants three resistances at once.
 *
 * A monster that already exceeds the limit by other means keeps what it has:
 * the rule bars a *bonus* from adding an immunity, it does not take one away.
 *
 * The competing reading, from the Fandom archive, is per-element: the grant is
 * refused on a resistance already at 100 and allowed on the others. It is not
 * implemented, because it cannot account for the boss that keeps a 70 it would
 * have lifted to 110, and because the Basin's is the one stated as a rule of
 * generation rather than as an observation. Unverified against the game files —
 * TODO row 256, and the four tests that pin this are the alarm if it changes.
 */
const grantResistance = (current, source) => {
    const granted = mapDamageTypes((type) => current[type] + modifierTotal(source.modifiers, 'resistancePercent', type));
    const adds = immunityCount(granted) > immunityCount(current);
    return adds && immunityCount(granted) > MAX_ELITE_IMMUNITIES ? current : granted;
};
const applyOverrides = (granted, effects) => {
    const overrides = effects.flatMap((source) => source.overrides);
    return mapDamageTypes((type) => overrides.reduce((value, override) => override.stat === 'resistancePercent' && override.damageType === type ? override.value : value, granted[type]));
};
/**
 * The damage types enhanced damage lifts. D2 applies it to the physical
 * component only; elemental damage on a weapon is flat, which is why a Champion
 * hits harder with its sword and identically with its fire.
 */
const ENHANCED_DAMAGE_TYPES = ['physical'];
const percentOf = (value, percent) => Math.floor((value * percent) / PERCENT);
const eliteRange = (base, modifiers, type, enhanced) => {
    const percent = ENHANCED_DAMAGE_TYPES.includes(type) ? PERCENT + enhanced : PERCENT;
    return {
        min: percentOf(base.min + modifierTotal(modifiers, 'damageMin', type), percent),
        max: percentOf(base.max + modifierTotal(modifiers, 'damageMax', type), percent),
    };
};
/**
 * Promotes a stat block to this pack's, in one pass.
 *
 * `stats` is the base monster the pack was rolled for — the pack's `level` is
 * that monster's level plus the rank's bonus, and the level-scaled damage was
 * derived from it when the pack was rolled.
 *
 * Life is not here because `CombatantStats` has no life pool: `eliteLife` is
 * the other half of applying a pack.
 */
export const applyElite = (stats, pack, role) => {
    const effects = effectsFor(pack, role);
    const scaled = effects.reduce((total, source) => mergeScaling(total, source.scaling), RESOLVED_NEUTRAL);
    const modifiers = aggregateModifiers(effects.flatMap((source) => source.modifiers));
    const enhanced = modifierTotal(modifiers, 'enhancedDamagePercent');
    return {
        ...stats,
        level: stats.level + scaled.levelBonus,
        attackRating: percentOf(stats.attackRating, PERCENT + scaled.attackRatingPercent) +
            modifierTotal(modifiers, 'attackRating'),
        defense: percentOf(stats.defense, PERCENT + modifierTotal(modifiers, 'enhancedDefensePercent')) +
            modifierTotal(modifiers, 'defense'),
        resistances: applyOverrides(effects.reduce(grantResistance, stats.resistances), effects),
        attack: {
            ...stats.attack,
            damage: mapDamageTypes((type) => eliteRange(stats.attack.damage[type], modifiers, type, enhanced)),
        },
    };
};
//# sourceMappingURL=elites.js.map